import { useState, useEffect, useCallback } from 'react'
import { bookingAPI } from '../services/api/bookings'
import { useAsyncOperation } from './useAsyncOperation'
import { createLogger } from '../utils/logger'

const logger = createLogger('useBookings')

/**
 * useBookings - fetches and manages the authenticated customer's bookings.
 * @param {string} [statusFilter] - optional status to filter by
 * Returns { bookings, loading, error, createBooking, cancelBooking, refresh }
 */
export function useBookings(statusFilter = '') {
  const [bookings, setBookings] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const { execute } = useAsyncOperation()

  const load = useCallback(() => {
    let cancelled = false
    setLoading(true)
    const params = statusFilter ? { status: statusFilter } : {}
    bookingAPI
      .getBookings(params)
      .then((res) => {
        if (!cancelled) setBookings(res?.data?.bookings || [])
      })
      .catch((err) => {
        if (!cancelled) {
          logger.error('Failed to load bookings', err)
          setError(err)
        }
      })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [statusFilter])

  useEffect(() => load(), [load])

  const createBooking = useCallback(async (data, { onSuccess, onError } = {}) => {
    await execute(() => bookingAPI.createBooking(data), {
      onSuccess: (res) => {
        setBookings((prev) => [res.data, ...prev])
        onSuccess?.(res.data)
      },
      onError,
    })
  }, [execute])

  const cancelBooking = useCallback(async (groupId, reason, { onSuccess, onError } = {}) => {
    await execute(() => bookingAPI.updateGroupStatus(groupId, 'cancelled', reason), {
      onSuccess: () => {
        setBookings((prev) =>
          prev.map((b) => b.group_id === groupId ? { ...b, status: 'cancelled' } : b)
        )
        onSuccess?.()
      },
      onError,
    })
  }, [execute])

  return { bookings, loading, error, createBooking, cancelBooking, refresh: load }
}
